/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { SignInWithAppleData, SocialLogin } from '../account-api/account.webservice';
import { Options } from '../options.interface';
import { writeWizbiiTokensInCookie } from '../utils';
import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { buildUrl } from '@wizbii-utils/angular/core';
import { JwtTokens } from '@wizbii-utils/angular/jwt';
import { LoginTokenResponse, UserOverview, toUserOverview } from '@wizbii/utils/models';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface AuthenticationApiConfig {
  baseUrl: string;
  appId: string;
  locale: string;
  platform: string;
  cookieDomains: Record<string, string>;
  defaultCookieDomain?: string;
}

export enum AuthenticationApiError {
  InvalidOldPasswordException = 'InvalidOldPasswordException',
  PasswordTooWeakException = 'PasswordTooWeakException',
  PasswordAlreadySetException = 'PasswordAlreadySetException',
}

export const AUTHENTICATION_API_CONFIG = new InjectionToken<AuthenticationApiConfig>('AUTHENTICATION_API_CONFIG');

@Injectable()
export class AuthenticationWebservice {
  readonly #http = inject(HttpClient);
  readonly #cookieService = inject(CookieService);
  readonly #authenticationApiConfig = inject(AUTHENTICATION_API_CONFIG);

  readonly #appId = this.#authenticationApiConfig.appId;
  readonly #baseUrl = this.#authenticationApiConfig.baseUrl;
  readonly #locale = this.#authenticationApiConfig.locale;
  readonly #platform = this.#authenticationApiConfig.platform;
  readonly #cookieDomains = this.#authenticationApiConfig.cookieDomains;
  readonly #defaultCookieDomain = this.#authenticationApiConfig.defaultCookieDomain;

  /**
   * Refresh the jwt
   * @param {Object} tokens Jwt and refresh token
   * @param {Object} [options] Angular Options for the request
   * @deprecated
   */
  refreshToken(tokens: JwtTokens, options?: Options): Observable<JwtTokens> {
    return this.#http.post<JwtTokens>(
      `${this.#baseUrl}/v1/jwt/refresh`,
      {
        appId: this.#appId,
        token: tokens.token,
        refreshToken: tokens.refreshToken,
      },
      options
    );
  }

  /**
   * Refresh the jwt
   * @param {Object} tokens Jwt and refresh token
   * @param {Object} [options] Angular Options for the request
   */
  refreshTokenV2(tokens: JwtTokens, appId: string, options?: Options): Observable<JwtTokens> {
    return this.#http
      .post<JwtTokens>(
        `${this.#baseUrl}/v1/jwt/refresh`,
        {
          appId: this.#appId,
          token: tokens.token,
          refreshToken: tokens.refreshToken,
        },
        options
      )
      .pipe(
        tap((newTokens) => {
          this.#writeTokensInCookie(newTokens, appId);
        })
      );
  }

  /**
   * Get jwt and refresh token for an application
   * @param {string} username Username to sign in
   * @param {string} password Password to sign in
   * @param {Object} [options] Angular Options for the request
   */
  signIn(username: string, password: string, appId: string, options?: Options): Observable<JwtTokens> {
    return this.#http
      .post<JwtTokens>(
        `${this.#baseUrl}/v2/auth/${this.#locale}`,
        {
          username,
          password,
          appId: this.#appId,
        },
        options
      )
      .pipe(
        tap((tokens) => {
          this.#writeTokensInCookie(tokens, appId);
        })
      );
  }

  /**
   * Send an email to recover the password
   * @param {string} email Email to send the password recover link
   * @param {string} [redirectUrl] Redirect url after recovery is completed
   * @param {string} [appId] appId from where the redirection is headed
   * @param {Object} [options] Angular Options for the request
   */
  getRecoveryEmail(email: string, redirectUrl?: string, appId?: string, options?: Options): Observable<void> {
    return this.#http.post<void>(
      `${this.#baseUrl}/v2/user/lost-password/${this.#locale}`,
      {
        email,
        redirectUrl,
        appId: appId ? appId : this.#appId,
        locale: this.#locale,
      },
      options
    );
  }

  /**
   * Change the password with a token
   * @param {string} passwordToken Token to recover the password
   * @param {string} password New password to set
   * @param {Object} [options] Angular Options for the request
   */
  recoverPassword(passwordToken: string, password: string, options?: Options): Observable<JwtTokens> {
    return this.#http.post<JwtTokens>(
      `${this.#baseUrl}/v1/user/lost-password/${passwordToken}`,
      {
        password,
        appId: this.#appId,
        locale: this.#locale,
      },
      options
    );
  }

  /**
   * Get an overview of an User
   * @param {string} id Id of the User
   * @param {Object} [options] Angular Options for the request
   */
  getUserOverview(id: string, options?: Options): Observable<UserOverview> {
    return this.#http.get<UserOverview>(`${this.#baseUrl}/v1/user/${id}/overview`, options).pipe(map(toUserOverview));
  }

  /**
   * Change the email of an User
   * @param {string} id Id of the User
   * @param {string} email Email of the User
   * @param {Object} [options] Angular Options for the request
   */
  changeEmail(id: string, email: string, options?: Options): Observable<UserOverview> {
    return this.#http.put<UserOverview>(
      buildUrl(`${this.#baseUrl}/v1/user/:id/email`, { id, email }),
      {
        email,
        appId: this.#appId,
      },
      options
    );
  }

  /**
   * Change the password of an User
   * @param {string} id Id of the User
   * @param {string} oldPassword Old password of the user
   * @param {string} newPassword New password of the User
   * @param {Object} [options] options Angular Options for the request
   */
  changePassword(id: string, oldPassword: string, newPassword: string, options?: Options): Observable<UserOverview> {
    return this.#http.put<UserOverview>(
      `${this.#baseUrl}/v1/user/${id}/password`,
      {
        oldPassword,
        newPassword,
        appId: this.#appId,
      },
      options
    );
  }

  /**
   * Set the password of an User
   * @param {string} id Id of the User
   * @param {string} newPassword Password of the User
   * @param {string} [redirectUrl] Redirect url after password is set
   * @param {Object} [options] options Angular Options for the request
   */
  setPassword(id: string, newPassword: string, redirectUrl?: string, options?: Options): Observable<UserOverview> {
    return this.#http.post<UserOverview>(
      `${this.#baseUrl}/v2/user/${id}/password/set/${this.#locale}`,
      {
        newPassword,
        appId: this.#appId,
        redirectUrl,
      },
      options
    );
  }

  /**
   * Set a random password for an user
   * @param {string} id Id of the User
   * @param {Object} [options] options Angular Options for the request
   */
  hardResetPassword(id: string, options?: Options): Observable<string> {
    return this.#http.post<string>(`${this.#baseUrl}/v1/user/${id}/password/reset`, options);
  }

  /**
   * Get Jwt from login token
   * @param {string} loginToken login token of the user
   * @param {Object} [options] Angular Options for the request
   */
  getJwtFromLoginToken(loginToken: string, options?: Options): Observable<JwtTokens> {
    return this.#http.post<JwtTokens>(
      `${this.#baseUrl}/v1/auth/login-token`,
      {
        loginToken,
        appId: this.#appId,
      },
      options
    );
  }

  /**
   * Get login token from profile od
   * @param {string} profileId profile id of the user
   */
  getLoginTokenByProfileId(
    profileId: string,
    payload?: { type?: string; duration?: number },
    options?: Options
  ): Observable<LoginTokenResponse> {
    return this.#http.post<LoginTokenResponse>(`${this.#baseUrl}/v1/user/${profileId}/login-token`, payload, options);
  }

  /**
   * Get recovery token from user id
   * @param {string} userId user id
   */
  getRecoveryTokenByUserId(userId: string, options?: Options): Observable<string> {
    return this.#http.get<string>(`${this.#baseUrl}/v1/user/${userId}/recovery-token`, options);
  }

  /**
   * Send password reset email
   * @param email {string} user's email address
   * @param appId {string}
   * @param {Object} [options] Angular Options for the request
   */
  sendResetPasswordEmail(email: string, appId: string, options?: Options): Observable<void> {
    return this.#http.post<void>(`${this.#baseUrl}/v1/user/lost-password`, { email, appId }, options);
  }

  /**
   * Unlink an authentication from the user
   * @param {string} id Id of the User
   * @param {string} authenticationType Type of the authentication to unlink
   * @param {Object} [options] options Angular Options for the request
   */
  unlinkSocial(id: string, authenticationType: string, options?: Options): Observable<UserOverview> {
    const builtOptions = { ...options, body: { type: authenticationType } };
    return this.#http.delete<UserOverview>(`${this.#baseUrl}/v1/user/${id}/authentication`, builtOptions);
  }

  /**
   * Associate user's account with Apple
   * @param {string} id Id of the User
   * @param {SignInWithAppleData} appleData
   * @param {Object} [options] options Angular Options for the request
   */
  associateApple(id: string, appleData: SignInWithAppleData, options?: Options): Observable<SocialLogin> {
    return this.#http.post<SocialLogin>(
      `${this.#baseUrl}/associate/${id}/apple`,
      {
        ...appleData,
        locale: this.#locale,
        appId: this.#appId,
      },
      options
    );
  }

  /**
   * Associate user's account with Google
   * @param {string} id Id of the User
   * @param {string} authCode return from google connect SDK
   * @param {Object} [options] options Angular Options for the request
   */
  associateGoogle(id: string, authCode: string, options?: Options): Observable<SocialLogin> {
    return this.#http.post<SocialLogin>(
      `${this.#baseUrl}/associate/${id}/google`,
      {
        authCode,
        locale: this.#locale,
        appId: this.#appId,
      },
      options
    );
  }

  /**
   * Associate user's account with Facebook
   * @param {string} id Id of the User
   * @param {string} accessToken return from facebook SDK
   * @param {Object} [options] options Angular Options for the request
   */
  associateFacebook(id: string, accessToken: string, options?: Options): Observable<SocialLogin> {
    return this.#http.post<SocialLogin>(
      `${this.#baseUrl}/associate/${id}/facebook`,
      {
        accessToken,
        locale: this.#locale,
        appId: this.#appId,
      },
      options
    );
  }

  #writeTokensInCookie(tokens: JwtTokens, appId: string): void {
    writeWizbiiTokensInCookie(this.#cookieService, tokens, {
      cookieDomains: this.#cookieDomains,
      locale: this.#locale,
      defaultCookieDomain: this.#defaultCookieDomain,
      platform: this.#platform,
      appId,
    });
  }
}
